import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Button, CustomSelect, FormField, Input, Textarea } from '@/components';
import { parseApiError } from '@/common/utils';
import {
  useQuotations,
  useQuotationProducts,
  useCreateCustomerQuotation,
} from '../hooks';
import {
  FIELD_LABEL_CLASS,
  formatQuoteMoney,
  DEFAULT_PDF_TEMPLATE_KEY,
  normalizeTaxRate,
  normalizeDeliveryCharge,
  sanitizeDeliveryChargeInput,
  showDeliveryLine,
} from '../utils';
import { PdfTemplatePicker } from './PdfTemplatePicker';
import { ContactPicker } from '../../appointments/components/ContactPicker';

// ─── Line item helpers ────────────────────────────────────────────────────────

const MAX_LINE_QTY = 999999;

const EMPTY_CUSTOM_ITEM = {
  type: 'custom', // 'catalog' | 'custom'
  product_id: null,
  product_name: '',
  unit_price: '',
  quantity: '1',
  moq: '1',
  tax_rate: '0',
};

function buildLineItems(items) {
  return items.map((item) => {
    const tax_rate = normalizeTaxRate(item.tax_rate) || '0.00';
    const quantity = Math.min(
      Math.max(parseInt(item.quantity, 10) || 1, 1),
      MAX_LINE_QTY,
    );
    if (item.type === 'catalog') {
      return {
        product_id: item.product_id,
        quantity,
        tax_rate,
      };
    }
    return {
      product_name: item.product_name.trim(),
      unit_price: item.unit_price,
      quantity,
      moq: parseInt(item.moq, 10) || 1,
      tax_rate,
    };
  });
}

function calcLiveTotal(items, deliveryCharge = 0) {
  let subtotal = 0;
  let tax = 0;
  const delivery = Math.max(parseFloat(deliveryCharge) || 0, 0);
  for (const item of items || []) {
    const price = Math.max(parseFloat(item.unit_price) || 0, 0);
    const qty = Math.min(
      Math.max(parseInt(item.quantity, 10) || 0, 0),
      MAX_LINE_QTY,
    );
    const taxRate = Math.min(Math.max(parseFloat(item.tax_rate) || 0, 0), 100);
    const line = price * qty;
    subtotal += line;
    tax += line * (taxRate / 100);
  }
  return { subtotal, tax, delivery, total: subtotal + tax + delivery };
}

// ─── Catalog product picker ───────────────────────────────────────────────────

function CatalogProductPicker({ companyId, quotationId, onAdd }) {
  const { data, isLoading } = useQuotationProducts(
    { companyId, quotationId },
    { enabled: !!quotationId },
  );
  const products = Array.isArray(data) ? data : data?.results || [];
  const available = products.filter((p) => p.is_available !== false);

  if (!quotationId) return null;

  return (
    <div className="space-y-1.5">
      <p className={FIELD_LABEL_CLASS}>Add from catalog</p>
      {isLoading ? (
        <p className="text-xs text-text-muted py-1">Loading products…</p>
      ) : available.length === 0 ? (
        <p className="text-xs text-text-muted py-1">
          No available products in this template.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {available.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() =>
                onAdd({
                  type: 'catalog',
                  product_id: p.id,
                  product_name: p.name,
                  unit_price: String(p.price || 0),
                  quantity: String(Math.max(p.moq || 1, 1)),
                  moq: String(p.moq || 1),
                  tax_rate:
                    p.tax_rate != null && p.tax_rate !== ''
                      ? String(p.tax_rate)
                      : '0',
                })
              }
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F0F7F0] border border-primary/20 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
            >
              <span>+</span>
              <span>{p.name}</span>
              <span className="text-text-muted font-normal">
                {'\u20b9'}
                {parseFloat(p.price).toLocaleString('en-IN')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Single line item row ─────────────────────────────────────────────────────

const LINE_GRID =
  'grid grid-cols-[minmax(0,1.5fr)_minmax(5.5rem,0.85fr)_minmax(4rem,0.55fr)_minmax(4.25rem,0.55fr)_minmax(5.5rem,0.9fr)] gap-2 items-center';

function sanitizeQty(raw) {
  const digits = String(raw ?? '').replace(/[^\d]/g, '');
  if (!digits) return '';
  const n = parseInt(digits.slice(0, 6), 10);
  if (!Number.isFinite(n) || n < 0) return '';
  return String(Math.min(n, MAX_LINE_QTY));
}

function sanitizeMoney(raw) {
  const cleaned = String(raw ?? '').replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n) || n < 0) return '';
  return cleaned;
}

function sanitizeTax(raw) {
  const cleaned = String(raw ?? '').replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n) || n < 0) return '';
  return cleaned;
}

function LineItemRow({ item, index, onChange, onRemove }) {
  const qty = Math.min(
    Math.max(parseInt(item.quantity, 10) || 0, 0),
    MAX_LINE_QTY,
  );
  const price = Math.max(parseFloat(item.unit_price) || 0, 0);
  const taxRate = Math.min(Math.max(parseFloat(item.tax_rate) || 0, 0), 100);
  const lineSubtotal = price * qty;
  const lineTax = lineSubtotal * (taxRate / 100);
  const lineTotal = lineSubtotal + lineTax;

  return (
    <div className="relative p-3 pr-8 rounded-xl bg-[#FAFAFA] border border-border-default group">
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-text-muted hover:text-red-500 p-0.5 rounded z-[1]"
        aria-label="Remove item"
      >
        <svg
          className="w-3.5 h-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className={LINE_GRID}>
        {/* Name */}
        <div className="min-w-0">
          {item.type === 'catalog' ? (
            <p className="text-sm font-medium text-text-primary truncate">
              {item.product_name}
            </p>
          ) : (
            <Input
              value={item.product_name}
              onChange={(e) => onChange(index, 'product_name', e.target.value)}
              placeholder="Item description"
              className="h-8 text-sm !min-w-0"
            />
          )}
        </div>

        {/* Unit price */}
        <Input
          type="text"
          inputMode="decimal"
          value={item.unit_price}
          onChange={(e) =>
            onChange(index, 'unit_price', sanitizeMoney(e.target.value))
          }
          placeholder="Price"
          disabled={item.type === 'catalog'}
          className="h-8 text-sm tabular-nums !px-2 !min-w-0"
          aria-label="Unit price"
        />

        {/* Quantity */}
        <Input
          type="text"
          inputMode="numeric"
          value={item.quantity}
          onChange={(e) =>
            onChange(index, 'quantity', sanitizeQty(e.target.value))
          }
          placeholder="Qty"
          className="h-8 text-sm tabular-nums text-center !px-1.5 !min-w-0"
          aria-label="Quantity"
        />

        {/* Tax % */}
        <Input
          type="text"
          inputMode="decimal"
          value={item.tax_rate ?? '0'}
          onChange={(e) =>
            onChange(index, 'tax_rate', sanitizeTax(e.target.value))
          }
          placeholder="Tax"
          className="h-8 text-sm tabular-nums text-center !px-1.5 !min-w-0"
          title="Tax rate (%)"
          aria-label="Tax rate percent"
        />

        {/* Line total */}
        <div className="min-w-0 text-right">
          <p
            className="text-xs font-semibold text-text-primary tabular-nums truncate"
            title={formatQuoteMoney(lineTotal, 'INR')}
          >
            {formatQuoteMoney(lineTotal, 'INR')}
          </p>
          {taxRate > 0 && (
            <p className="text-[10px] text-text-muted truncate">
              +tax {taxRate}%
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main form ────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  quotation_id: '',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  notes: '',
  delivery_term: '',
  delivery_charge: '',
  template_key: DEFAULT_PDF_TEMPLATE_KEY,
};

/**
 * CustomerQuotationForm
 * Rendered inside a SideDrawer to manually create a customer quotation.
 *
 * @param {Object} props
 * @param {string|number} props.companyId
 * @param {() => void} props.onCancel
 * @param {(data: Object) => void} [props.onSuccess]
 */
export function CustomerQuotationForm({ companyId, onCancel, onSuccess }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [contactId, setContactId] = useState(null);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const userSelectedTemplateRef = useRef(false);

  const createMutation = useCreateCustomerQuotation();
  const isPending = createMutation.isPending;

  // Quotation template options
  const { data: quotationsData, isLoading: isLoadingQ } = useQuotations(
    companyId,
    { is_active: true },
  );
  const quotations = Array.isArray(quotationsData)
    ? quotationsData
    : quotationsData?.results || [];

  // Live totals (per-line tax)
  const { subtotal, tax, delivery, total } = calcLiveTotal(
    items,
    form.delivery_charge,
  );
  const selectedQuotation = quotations.find(
    (q) => String(q.id) === String(form.quotation_id),
  );

  // Pre-fill PDF template when catalog changes (only if user hasn't manually selected one)
  useEffect(() => {
    if (!selectedQuotation) return;
    setForm((f) => ({
      ...f,
      ...(!userSelectedTemplateRef.current && selectedQuotation.template_key
        ? { template_key: selectedQuotation.template_key }
        : {}),
      delivery_term: selectedQuotation.delivery_term || '',
      delivery_charge:
        selectedQuotation.delivery_charge != null &&
        Number(selectedQuotation.delivery_charge) !== 0
          ? String(selectedQuotation.delivery_charge)
          : '',
    }));
  }, [selectedQuotation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  // ── Line items ─────────────────────────────────────────────────────────────

  const addItem = (item) => setItems((prev) => [...prev, item]);

  const addCustomItem = () =>
    setItems((prev) => [...prev, { ...EMPTY_CUSTOM_ITEM }]);

  const updateItem = (index, field, value) =>
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );

  const removeItem = (index) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  // ── Validation ─────────────────────────────────────────────────────────────

  function validateStep1() {
    const errs = {};
    if (!form.quotation_id) errs.quotation_id = 'Select a quotation template.';
    return errs;
  }

  function validateStep2() {
    const errs = {};
    if (!contactId || !form.customer_name.trim())
      errs.customer_name = 'Select a customer from the list.';
    if (!form.customer_phone.trim())
      errs.customer_phone = 'Selected customer has no phone number.';
    if (items.length === 0) errs.items = 'Add at least one line item.';
    items.forEach((item, i) => {
      if (item.type === 'custom' && !item.product_name.trim()) {
        errs[`item_${i}_name`] = `Item ${i + 1}: description is required.`;
      }
      if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
        errs[`item_${i}_price`] =
          `Item ${i + 1}: unit price must be greater than 0.`;
      }
    });
    if (form.delivery_charge !== '') {
      const charge = Number(form.delivery_charge);
      if (Number.isNaN(charge) || charge < 0) {
        errs.delivery_charge = 'Enter a delivery amount of 0 or more.';
      }
    }
    return errs;
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = (e) => {
    e.preventDefault();

    if (step === 1) {
      const errs = validateStep1();
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }
      setStep(2);
      return;
    }

    const errs = validateStep2();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const delivery_charge = normalizeDeliveryCharge(form.delivery_charge);
    if (delivery_charge == null) {
      setErrors((e) => ({
        ...e,
        delivery_charge: 'Enter a valid delivery amount.',
      }));
      return;
    }

    const payload = {
      quotation_id: parseInt(form.quotation_id, 10),
      customer_name: form.customer_name.trim(),
      customer_phone: form.customer_phone.trim(),
      delivery_term: form.delivery_term.trim(),
      delivery_charge,
      ...(form.customer_email.trim()
        ? { customer_email: form.customer_email.trim() }
        : {}),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      template_key: form.template_key || DEFAULT_PDF_TEMPLATE_KEY,
      items: buildLineItems(items),
    };

    createMutation.mutate(
      { companyId, payload },
      {
        onSuccess: (data) => {
          toast.success(`Quotation ${data.quote_number} created successfully.`);
          onSuccess?.(data);
        },
        onError: (err) => {
          const parsed = parseApiError(err);
          if (parsed.fieldErrors) setErrors(parsed.fieldErrors);
          toast.error(parsed.message || 'Failed to create quotation.');
        },
      },
    );
  };

  const currency = selectedQuotation?.currency || 'INR';

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-full gap-5"
      noValidate
    >
      {step === 1 && (
        <>
          <section className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-0.5">
            <div className="flex flex-col gap-3 shrink-0">
              <p className="text-xs font-semibold uppercase tracking-widest text-text-muted">
                Quotation Template
              </p>
              <FormField error={errors.quotation_id}>
                <label className={FIELD_LABEL_CLASS} htmlFor="cqf-quotation">
                  Template <span className="text-red-500">*</span>
                </label>
                <CustomSelect
                  value={form.quotation_id}
                  onChange={(val) => {
                    set('quotation_id', val);
                    setItems([]); // Reset items when template changes
                  }}
                  options={quotations.map((q) => ({
                    value: String(q.id),
                    label: q.title + (q.quote_code ? ` (${q.quote_code})` : ''),
                  }))}
                  placeholder={
                    isLoadingQ ? 'Loading templates…' : 'Select a template…'
                  }
                  isLoading={isLoadingQ}
                  error={errors.quotation_id}
                  searchable
                  searchPlaceholder="Search templates…"
                  className="h-10"
                />
              </FormField>
            </div>

            <div className="flex flex-col gap-2.5 min-h-0">
              <PdfTemplatePicker
                companyId={companyId}
                value={form.template_key}
                onChange={(key) => {
                  userSelectedTemplateRef.current = true;
                  set('template_key', key);
                }}
                disabled={isPending}
                compact
                showSearch
                showCategoryFilter
                showDescription
                label="PDF Design"
                helperText="Inherited from the catalog. Change if this customer needs a different look."
              />
            </div>
          </section>

          <div className="flex items-center justify-end gap-2 pt-4 mt-auto border-t border-border-default shrink-0">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isPending}
              className="h-10 px-4 text-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="h-10 px-5 text-sm"
            >
              Next: Customer Details
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="flex flex-col gap-5 flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-0.5">
            {/* ── Customer section ── */}
            <section className="flex flex-col gap-3 shrink-0">
              <p className="text-xs font-semibold uppercase tracking-widest text-text-muted">
                Customer
              </p>

              <FormField error={errors.customer_name}>
                <label className={FIELD_LABEL_CLASS} htmlFor="cqf-name">
                  Name <span className="text-red-500">*</span>
                </label>
                <ContactPicker
                  companyId={companyId}
                  value={contactId}
                  selectedLabel={form.customer_name}
                  disabled={isPending}
                  onChange={({ id, name, contact }) => {
                    setContactId(id);
                    const phone =
                      contact?.phone_normalized ||
                      (contact?.country_code && contact?.phone
                        ? `+${String(contact.country_code).replace(/^\+/, '')}${contact.phone}`
                        : contact?.phone || '');
                    setForm((f) => ({
                      ...f,
                      customer_name: name || '',
                      customer_phone: phone ? String(phone) : '',
                      customer_email: contact?.email || '',
                    }));
                    setErrors((e) => ({
                      ...e,
                      customer_name: undefined,
                      customer_phone: undefined,
                      customer_email: undefined,
                    }));
                  }}
                />
              </FormField>

              <FormField error={errors.customer_phone}>
                <label className={FIELD_LABEL_CLASS} htmlFor="cqf-phone">
                  WhatsApp Phone <span className="text-red-500">*</span>
                </label>
                <Input
                  id="cqf-phone"
                  type="tel"
                  value={form.customer_phone}
                  readOnly
                  disabled
                  placeholder="Fills when you select a customer"
                  className="h-10 text-sm"
                />
              </FormField>

              <FormField error={errors.customer_email}>
                <label className={FIELD_LABEL_CLASS} htmlFor="cqf-email">
                  Email
                </label>
                <Input
                  id="cqf-email"
                  type="email"
                  value={form.customer_email}
                  readOnly
                  disabled
                  placeholder="Fills when you select a customer"
                  className="h-10 text-sm"
                />
              </FormField>
            </section>

            {/* ── Line items section ── */}
            <section className="flex flex-col gap-3 shrink-0">
              <p className="text-xs font-semibold uppercase tracking-widest text-text-muted">
                Line Items
              </p>

              {/* Catalog product pill chips */}
              <CatalogProductPicker
                companyId={companyId}
                quotationId={form.quotation_id}
                onAdd={addItem}
              />

              {/* Item rows */}
              {items.length > 0 && (
                <div className="space-y-2">
                  {/* Column headers — same grid as rows */}
                  <div className={`${LINE_GRID} px-3 pr-8 py-1`}>
                    <span className="text-[10px] font-medium text-text-muted uppercase tracking-wide">
                      Description
                    </span>
                    <span className="text-[10px] font-medium text-text-muted uppercase tracking-wide">
                      Unit Price
                    </span>
                    <span className="text-[10px] font-medium text-text-muted uppercase tracking-wide text-center">
                      Qty
                    </span>
                    <span className="text-[10px] font-medium text-text-muted uppercase tracking-wide text-center">
                      Tax (%)
                    </span>
                    <span className="text-[10px] font-medium text-text-muted uppercase tracking-wide text-right">
                      Total
                    </span>
                  </div>
                  {items.map((item, i) => (
                    <LineItemRow
                      key={i}
                      item={item}
                      index={i}
                      onChange={updateItem}
                      onRemove={removeItem}
                    />
                  ))}
                </div>
              )}

              {/* Add custom item */}
              <button
                type="button"
                onClick={addCustomItem}
                className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors py-1"
              >
                <svg
                  className="w-3.5 h-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add custom line item
              </button>

              {errors.items && (
                <p className="text-xs text-red-500">{errors.items}</p>
              )}

              {/* Per-item field errors */}
              {Object.entries(errors)
                .filter(([k]) => k.startsWith('item_'))
                .map(([k, v]) => (
                  <p key={k} className="text-xs text-red-500">
                    {v}
                  </p>
                ))}
            </section>

            <section className="flex flex-col gap-3 shrink-0">
              <p className="text-xs font-semibold uppercase tracking-widest text-text-muted">
                Delivery
              </p>
              <FormField error={errors.delivery_term}>
                <label
                  className={FIELD_LABEL_CLASS}
                  htmlFor="cqf-delivery-term"
                >
                  Delivery terms
                </label>
                <Input
                  id="cqf-delivery-term"
                  value={form.delivery_term}
                  onChange={(e) => set('delivery_term', e.target.value)}
                  placeholder="Ex-Works, Door Delivery, F.O.R."
                  maxLength={255}
                  className="h-10 text-sm"
                />
              </FormField>
              <FormField error={errors.delivery_charge}>
                <label
                  className={FIELD_LABEL_CLASS}
                  htmlFor="cqf-delivery-charge"
                >
                  Delivery / freight amount
                </label>
                <Input
                  id="cqf-delivery-charge"
                  type="text"
                  inputMode="decimal"
                  value={form.delivery_charge}
                  onChange={(e) =>
                    set(
                      'delivery_charge',
                      sanitizeDeliveryChargeInput(e.target.value),
                    )
                  }
                  placeholder="0.00"
                  className="h-10 text-sm"
                />
                <p className="text-[11px] text-text-muted">
                  Leave empty or 0 if delivery is not charged. Copied from the
                  catalog; override for this customer.
                </p>
              </FormField>
            </section>

            {/* ── Notes section ── */}
            <section className="flex flex-col gap-3 shrink-0">
              <p className="text-xs font-semibold uppercase tracking-widest text-text-muted">
                Notes
              </p>

              <FormField>
                <label className={FIELD_LABEL_CLASS} htmlFor="cqf-notes">
                  Notes
                </label>
                <Textarea
                  id="cqf-notes"
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="Urgent delivery requested…"
                  rows={2}
                  className="text-sm"
                />
              </FormField>
            </section>

            {/* ── Live totals summary ── */}
            {items.length > 0 && (
              <div className="rounded-xl bg-[#F5F5F5] p-3.5 space-y-1.5 shrink-0">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Subtotal</span>
                  <span className="font-medium text-text-primary">
                    {formatQuoteMoney(subtotal, currency)}
                  </span>
                </div>
                {tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">
                      Tax (line items)
                    </span>
                    <span className="font-medium text-text-primary">
                      {formatQuoteMoney(tax, currency)}
                    </span>
                  </div>
                )}
                {showDeliveryLine(form.delivery_term, delivery) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">
                      {form.delivery_term.trim() || 'Delivery'}
                    </span>
                    <span className="font-medium text-text-primary">
                      {formatQuoteMoney(delivery, currency)}
                    </span>
                  </div>
                )}
                <div className="border-t border-border-default pt-1.5 flex justify-between">
                  <span className="font-bold text-text-primary text-sm">
                    Total
                  </span>
                  <span className="font-bold text-primary text-sm">
                    {formatQuoteMoney(total, currency)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* ── Action buttons ── */}
          <div className="flex items-center justify-end gap-2 pt-4 mt-auto border-t border-border-default shrink-0">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStep(1)}
              disabled={isPending}
              className="h-10 px-4 text-sm"
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="h-10 px-5 text-sm"
            >
              {isPending ? 'Creating…' : 'Create Quotation'}
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
